import { useMemo, useState } from "react";

import styled from "styled-components";

import Dropdown from "components/atoms/Dropdown";
import Icon from "components/atoms/Icon";
import Tooltip from "components/atoms/Tooltip";

import { useCodeMirrorViewContext } from "../codemirror-view-context.js";
import { useSelectionContext } from "../contexts/selection-context.js";
import { useTableContext } from "../contexts/table-context.js";
import { useTabularContext } from "../contexts/tabular-context.js";
import {
    BorderTheme,
    insertColumn,
    insertRow,
    mergeCells,
    moveCaption,
    removeCaption,
    removeColumnWidths,
    removeNodes,
    removeRowOrColumns,
    setAlignment,
    setBorders,
    unmergeCells,
} from "./commands.js";

const ToolbarContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
    margin: 0 auto 12px;
    padding: 6px;
    width: fit-content;
    max-width: 100%;
    border-radius: 10px;
    background: var(--table-generator-toolbar-background);
    box-shadow: 0 2px 4px 0 var(--table-generator-toolbar-shadow-color);
`;

const Group = styled.div`
    display: inline-flex;
    gap: 6px;
    align-items: center;
    padding-right: 6px;
    margin-right: 2px;
    border-right: 1px solid var(--table-generator-divider-color);

    &:last-child {
        border-right: none;
        padding-right: 0;
        margin-right: 0;
    }
`;

const ActionButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    min-width: 34px;
    height: 34px;
    padding: 0 10px;
    border: 1px solid transparent;
    border-radius: 8px;
    background: ${(props) =>
        props.$active ? "var(--table-generator-toolbar-button-active-background)" : "transparent"};
    color: var(--table-generator-toolbar-button-color);
    font-size: 12px;
    font-weight: 600;
    cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
    opacity: ${(props) => (props.disabled ? 0.48 : 1)};

    &:hover {
        background: ${(props) =>
            props.disabled
                ? props.$active
                    ? "var(--table-generator-toolbar-button-active-background)"
                    : "transparent"
                : "var(--table-generator-toolbar-button-hover-background)"};
    }
`;

const MenuPopover = styled.div`
    min-width: 180px;
    padding: 6px;
    border: 1px solid #d8e1ec;
    border-radius: 10px;
    background: #fff;
    box-shadow: 0 12px 24px rgba(15, 23, 42, 0.12);
`;

const MenuItem = styled.button`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
    min-height: 34px;
    padding: 0 10px;
    border: 0;
    border-radius: 8px;
    background: ${(props) => (props.$active ? "rgba(15, 143, 111, 0.12)" : "transparent")};
    color: ${(props) => (props.$active ? "#0f8f6f" : "#42576b")};
    text-align: left;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
        background: rgba(15, 143, 111, 0.08);
    }
`;

const Divider = styled.hr`
    margin: 6px 0;
    border: 0;
    border-top: 1px solid #e7edf4;
`;

function ToolbarIcon({ name, color }) {
    return <Icon name={name} size="13px" color={color || "var(--table-generator-toolbar-button-color)"} />;
}

function ToolbarButton({ label, icon, onClick, disabled = false, active = false, tooltip }) {
    const button = (
        <ActionButton
            type="button"
            $active={active}
            disabled={disabled}
            onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
            }}
            onClick={disabled ? undefined : onClick}
        >
            {icon ? <ToolbarIcon name={icon} /> : null}
            {label ? <span>{label}</span> : null}
        </ActionButton>
    );

    return tooltip ? <Tooltip message={tooltip}>{button}</Tooltip> : button;
}

function ToolbarMenu({ trigger, items, width = "220px" }) {
    const [open, setOpen] = useState(false);
    return (
        <Dropdown
            open={open}
            onOpenChange={setOpen}
            placement="bottom-start"
            offset={6}
            anchor={trigger(() => setOpen((prev) => !prev))}
            contentMinWidth={width}
        >
            <MenuPopover>
                {items.map((item, index) =>
                    item.type === "divider" ? (
                        <Divider key={`divider-${index}`} />
                    ) : (
                        <MenuItem
                            key={item.id}
                            type="button"
                            $active={item.active}
                            disabled={item.disabled}
                            onClick={() => {
                                if (item.disabled) return;
                                item.onClick();
                                setOpen(false);
                            }}
                        >
                            <span>{item.label}</span>
                            {item.active ? <ToolbarIcon name="check" color="#0f8f6f" /> : null}
                        </MenuItem>
                    ),
                )}
            </MenuPopover>
        </Dropdown>
    );
}

export function Toolbar() {
    const { selection, setSelection } = useSelectionContext();
    const view = useCodeMirrorViewContext();
    const { positions, rowSeparators, cellSeparators, tableEnvironment, table, directTableChild } = useTableContext();
    const { showHelp, openColumnWidthModal } = useTabularContext();

    const captionPosition = useMemo(() => {
        if (!tableEnvironment?.caption) return "no_caption";
        if (tableEnvironment.caption.from < positions.tabular.from) return "above";
        return "below";
    }, [positions, tableEnvironment]);

    const currentAlignment = useMemo(() => {
        if (!selection) return undefined;
        if (selection.isMergedCellSelected(table)) {
            const cell = table.getCell(selection.from.row, selection.from.cell);
            return cell.multiColumn?.columns.specification[0]?.alignment;
        }
        const { minX, maxX } = selection.normalized();
        const alignment = table.columns[minX].alignment;
        for (let x = minX + 1; x <= maxX; x += 1) {
            if (table.columns[x].alignment !== alignment) {
                return undefined;
            }
        }
        return alignment;
    }, [selection, table]);

    if (!selection) {
        return null;
    }

    const columnsToInsert = selection.maximumCellWidth(table);
    const rowsToInsert = selection.height();
    const onlyFixedWidthColumnsSelected = selection.isOnlyFixedWidthColumns(table);
    const onlyNonFixedWidthColumnsSelected = selection.isOnlyNonFixedWidthColumns(table);

    const captionItems = [
        {
            id: "caption-none",
            label: "No caption",
            active: captionPosition === "no_caption",
            onClick: () => removeCaption(view, tableEnvironment),
        },
        {
            id: "caption-above",
            label: "Caption above",
            active: captionPosition === "above",
            onClick: () => moveCaption(view, positions, "above", tableEnvironment),
        },
        {
            id: "caption-below",
            label: "Caption below",
            active: captionPosition === "below",
            onClick: () => moveCaption(view, positions, "below", tableEnvironment),
        },
    ];

    const borderItems = [
        {
            id: "border-none",
            label: "No borders",
            active: table.getBorderTheme() === BorderTheme.NO_BORDERS,
            onClick: () => setBorders(view, BorderTheme.NO_BORDERS, positions, rowSeparators, table),
        },
        {
            id: "border-all",
            label: "All borders",
            active: table.getBorderTheme() === BorderTheme.FULLY_BORDERED,
            onClick: () => setBorders(view, BorderTheme.FULLY_BORDERED, positions, rowSeparators, table),
        },
        {
            id: "border-booktabs",
            label: "Booktabs",
            active: table.getBorderTheme() === BorderTheme.BOOKTABS,
            onClick: () => setBorders(view, BorderTheme.BOOKTABS, positions, rowSeparators, table),
        },
    ];

    const alignmentItems = [
        {
            id: "align-left",
            label: "Left",
            active: currentAlignment === "left",
            onClick: () => setAlignment(view, selection, "left", positions, table),
        },
        {
            id: "align-center",
            label: "Center",
            active: currentAlignment === "center",
            onClick: () => setAlignment(view, selection, "center", positions, table),
        },
        {
            id: "align-right",
            label: "Right",
            active: currentAlignment === "right",
            onClick: () => setAlignment(view, selection, "right", positions, table),
        },
        ...(onlyFixedWidthColumnsSelected && !selection.isMergedCellSelected(table)
            ? [
                  {
                      id: "align-justify",
                      label: "Justify",
                      active: currentAlignment === "paragraph",
                      onClick: () => setAlignment(view, selection, "paragraph", positions, table),
                  },
              ]
            : []),
    ];

    const widthItems = [
        {
            id: "width-auto",
            label: "Stretch width to text",
            active: onlyNonFixedWidthColumnsSelected,
            disabled: !selection.isAnyColumnSelected(table),
            onClick: () => removeColumnWidths(view, selection, positions, table),
        },
        {
            id: "width-fixed",
            label: onlyFixedWidthColumnsSelected ? "Fixed width" : "Fixed width wrap text",
            active: onlyFixedWidthColumnsSelected,
            disabled: !selection.isAnyColumnSelected(table),
            onClick: openColumnWidthModal,
        },
        ...(onlyFixedWidthColumnsSelected
            ? [
                  { type: "divider" },
                  {
                      id: "width-set",
                      label: "Set column width",
                      onClick: openColumnWidthModal,
                  },
              ]
            : []),
    ];

    const addItems = [
        {
            id: "insert-column-left",
            label: columnsToInsert === 1 ? "Insert column left" : `Insert ${columnsToInsert} columns left`,
            onClick: () => setSelection(insertColumn(view, selection, positions, false, table)),
        },
        {
            id: "insert-column-right",
            label: columnsToInsert === 1 ? "Insert column right" : `Insert ${columnsToInsert} columns right`,
            onClick: () => setSelection(insertColumn(view, selection, positions, true, table)),
        },
        { type: "divider" },
        {
            id: "insert-row-above",
            label: rowsToInsert === 1 ? "Insert row above" : `Insert ${rowsToInsert} rows above`,
            onClick: () => setSelection(insertRow(view, selection, positions, false, rowSeparators, table)),
        },
        {
            id: "insert-row-below",
            label: rowsToInsert === 1 ? "Insert row below" : `Insert ${rowsToInsert} rows below`,
            onClick: () => setSelection(insertRow(view, selection, positions, true, rowSeparators, table)),
        },
    ];

    const canAlign = selection.isColumnSelected(selection.from.cell, table) || selection.isMergedCellSelected(table);

    return (
        <ToolbarContainer className="table-generator-floating-toolbar">
            <Group>
                <ToolbarMenu
                    trigger={(toggle) => (
                        <ToolbarButton
                            label={
                                captionPosition === "no_caption"
                                    ? "No caption"
                                    : captionPosition === "above"
                                      ? "Caption above"
                                      : "Caption below"
                            }
                            icon="table"
                            onClick={toggle}
                            disabled={!tableEnvironment || !directTableChild}
                            tooltip="Caption"
                        />
                    )}
                    items={captionItems}
                />
                <ToolbarMenu
                    trigger={(toggle) => (
                        <ToolbarButton
                            label={
                                table.getBorderTheme() === BorderTheme.FULLY_BORDERED
                                    ? "All borders"
                                    : table.getBorderTheme() === BorderTheme.BOOKTABS
                                      ? "Booktabs"
                                      : table.getBorderTheme() === BorderTheme.NO_BORDERS
                                        ? "No borders"
                                        : "Custom borders"
                            }
                            icon="table"
                            onClick={toggle}
                            tooltip="Borders"
                        />
                    )}
                    items={borderItems}
                />
            </Group>

            <Group>
                <ToolbarMenu
                    trigger={(toggle) => (
                        <ToolbarButton
                            label="Align"
                            icon={
                                currentAlignment === "center"
                                    ? "alignCenter"
                                    : currentAlignment === "right"
                                      ? "alignRight"
                                      : "alignLeft"
                            }
                            onClick={toggle}
                            disabled={!canAlign}
                            tooltip="Alignment"
                        />
                    )}
                    items={alignmentItems}
                />

                <ToolbarMenu
                    trigger={(toggle) => (
                        <ToolbarButton
                            label="Width"
                            icon="expand"
                            onClick={toggle}
                            disabled={!selection.isAnyColumnSelected(table)}
                            tooltip="Adjust column width"
                        />
                    )}
                    items={widthItems}
                />

                <ToolbarButton
                    label={selection.isMergedCellSelected(table) ? "Unmerge" : "Merge"}
                    icon="table"
                    onClick={() => {
                        if (selection.isMergedCellSelected(table)) {
                            unmergeCells(view, selection, table);
                        } else {
                            mergeCells(view, selection, table);
                        }
                    }}
                    disabled={!selection.isMergedCellSelected(table) && !selection.isMergeableCells(table)}
                    tooltip={selection.isMergedCellSelected(table) ? "Unmerge cells" : "Merge cells"}
                />

                <ToolbarButton
                    label="Delete"
                    icon="delete"
                    onClick={() => setSelection(removeRowOrColumns(view, selection, positions, cellSeparators, table))}
                    disabled={
                        (!selection.isAnyRowSelected(table) && !selection.isAnyColumnSelected(table)) ||
                        !selection.eq(selection.explode(table))
                    }
                    tooltip="Delete row or column"
                />
            </Group>

            <Group>
                <ToolbarMenu
                    trigger={(toggle) => <ToolbarButton label="Insert" icon="plus" onClick={toggle} tooltip="Insert" />}
                    items={addItems}
                />
            </Group>

            <Group>
                <ToolbarButton
                    label="Remove table"
                    icon="delete"
                    onClick={() => {
                        removeNodes(view, tableEnvironment?.table ?? positions.tabular);
                        view.focus();
                    }}
                    tooltip="Delete table"
                />
                <ToolbarButton label="Help" icon="question" onClick={showHelp} tooltip="Help" />
            </Group>
        </ToolbarContainer>
    );
}
